/**
 * The gate. One component, wrapping the whole shell, rather than a check per screen.
 *
 * Three states, and conflating the first two is the bug this is written to avoid:
 *
 * - **`checking`** renders nothing. The bootstrap `me()` is in flight, and redirecting to
 *   login here would flash the login screen on every page load for someone whose cookie is
 *   perfectly valid — and worse, would replace the URL they asked for.
 * - **`out`** redirects to `/login`, **remembering where they were going**. An operator
 *   opening a bookmarked `/system` during an incident should land on `/system` after signing
 *   in, not on the dashboard with the incident one more click away.
 * - **`in`** renders the app.
 *
 * Nothing here is a security control. The API refuses every request without a session on its
 * own (H9: *"a rule that exists only in the API can be bypassed"* — a rule that exists only
 * in the **UI** is not a rule at all). This is navigation.
 */

import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { useAuth } from './AuthProvider';

export function RequireAuth(): React.JSX.Element | null {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'checking') return null;
  if (status === 'out') {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return <Outlet />;
}

/**
 * The inverse, for `/login` itself: someone already signed in has no business there.
 *
 * Without this, a signed-in operator who types `/login` gets the password form, signs in
 * again, and creates a second session for no reason — which then shows up in the session
 * list they are supposed to be able to reason about.
 */
export function RedirectIfSignedIn({ children }: { children: React.ReactNode }): React.ReactNode {
  const { status } = useAuth();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from;

  if (status === 'checking') return null;
  if (status === 'in') return <Navigate to={from && from !== '/login' ? from : '/'} replace />;
  return children;
}
